/**
 * Utility for validating usernames/full names and filtering inappropriate language and reserved words.
 * Supports both English (en) and Arabic (ar).
 */

// List of reserved system words (English and Arabic)
const RESERVED_WORDS = [
  // English reserved words
  "admin",
  "administrator",
  "moderator",
  "mod",
  "system",
  "sysadmin",
  "support",
  "helpdesk",
  "official",
  "staff",
  "null",
  "undefined",
  "root",
  "superuser",
  "kfm",
  "kfmadmin",
  "guest",
  "anonymous",
  "security",
  "webmaster",

  // Arabic reserved words
  "ادمن",
  "أدمن",
  "مدير",
  "مشرف",
  "نظام",
  "دعم",
  "مساعده",
  "مساعدة",
  "رسمي",
  "حساب_رسمي",
  "حساب رسمي",
  "جذر",
  "ضيف",
  "مجهول",
  "امن",
  "أمن",
];

// List of English inappropriate / profane / offensive terms
const ENGLISH_PROFANITY = [
  "fuck",
  "fck",
  "fuk",
  "shit",
  "sht",
  "bitch",
  "btch",
  "cunt",
  "asshole",
  "ass",
  "bastard",
  "dick",
  "pussy",
  "cock",
  "whore",
  "slut",
  "nigger",
  "nigga",
  "faggot",
  "retard",
  "penis",
  "vagina",
  "porn",
  "porno",
  "sex",
  "nude",
  "naked",
  "anal",
  "clitoris",
  "dildo",
  "ejaculate",
  "fetish",
  "masterbate",
  "masturbate",
  "orgasm",
  "pecker",
  "prostitute",
  "twat",
  "wanker",
  "prick",
  "slutty",
  "bitchy",
  "boob",
  "boobs",
  "tit",
  "tits",
  "horny",
  "jerk",
  "jerkoff",
  "cum",
  "milf",
  "wtf",
  "nazi",
  "hitler",
];

// List of Arabic inappropriate / profane / offensive terms
const ARABIC_PROFANITY = [
  "كلب",
  "حمار",
  "حقير",
  "عاهرة",
  "شرموط",
  "شرموطه",
  "شرموطة",
  "قحبة",
  "قحبه",
  "كس",
  "زبي",
  "طيز",
  "مني",
  "منيوك",
  "عرص",
  "خول",
  "ديوث",
  "منحرف",
  "ساقط",
  "قذر",
  "وسخ",
  "قواد",
  "زب",
  "ابن_الكلب",
  "ابن الكلب",
  "ابن_الشرموطة",
  "ابن الشرموطة",
  "ابن الشرموطه",
  "منيكة",
  "منيكه",
  "قحاب",
  "طيزك",
  "كسختك",
  "كس اختك",
  "كسمك",
  "كس امك",
  "عهر",
  "داعر",
  "دعارة",
  "دعارها",
  "خرائي",
  "خرا",
  "خرة",
  "زق",
  "تفاهة",
  "سافل",
  "تافه",
  "قحباء",
];

/**
 * Remove Arabic Tashkeel (diacritics), Tatweel, and normalize letters.
 */
export const normalizeArabic = (text: string): string => {
  if (!text) return "";
  return text
    .replace(/[\u064B-\u065F\u0670]/g, "") // Remove Tashkeel
    .replace(/\u0640/g, "") // Remove Tatweel (Kashida)
    .replace(/[أإآآ]/g, "ا") // Normalize Alefs
    .replace(/ة/g, "ه") // Normalize Teh Marbuta
    .replace(/ى/g, "ي"); // Normalize Alef Maksura
};

/**
 * Replace common Leetspeak / character substitutions (e.g. @ -> a, 0 -> o).
 */
export const normalizeLeetspeak = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/@/g, "a")
    .replace(/4/g, "a")
    .replace(/\$/g, "s")
    .replace(/5/g, "s")
    .replace(/0/g, "o")
    .replace(/1/g, "i")
    .replace(/!/g, "i")
    .replace(/3/g, "e")
    .replace(/7/g, "h")
    .replace(/8/g, "g")
    .replace(/9/g, "q");
};

/**
 * Remove non-alphanumeric separators and collapse repeating characters.
 * E.g. "f.u.c.k" -> "fuck", "a_d_m_i_n" -> "admin", "fuuuuck" -> "fuck".
 */
const simplifyString = (text: string): string => {
  if (!text) return "";
  // Strip non-alphanumeric (except spaces)
  const cleaned = text.replace(/[^a-zA-Z0-9\u0600-\u06FF]/g, "");
  // Collapse repeated characters (3+ of same char down to 1)
  return cleaned.replace(/(.)\1{2,}/gi, "$1");
};

/**
 * Check if a given username or name contains inappropriate language or reserved words.
 * Returns true if the username is INAPPROPRIATE (should be blocked).
 */
export const isInappropriateUsername = (name: string | null | undefined): boolean => {
  if (!name || typeof name !== "string") return false;

  const rawLower = name.trim().toLowerCase();
  if (!rawLower) return false;

  const normalizedAr = normalizeArabic(rawLower);
  const normalizedLeet = normalizeLeetspeak(normalizedAr);
  const simplified = simplifyString(normalizedLeet);

  // 1. Check Reserved Words (exact match or simplified match)
  for (const reserved of RESERVED_WORDS) {
    const normReserved = normalizeArabic(reserved).toLowerCase();
    if (
      rawLower === normReserved ||
      normalizedAr === normReserved ||
      simplified === normReserved ||
      rawLower.startsWith(`${normReserved} `) ||
      rawLower.endsWith(` ${normReserved}`) ||
      rawLower.includes(` ${normReserved} `)
    ) {
      return true;
    }
  }

  // 2. Check English Profanity
  for (const word of ENGLISH_PROFANITY) {
    // Exact word match in raw text or normalized Leetspeak
    const wordRegex = new RegExp(`\\b${word}\\b`, "i");
    if (wordRegex.test(rawLower) || wordRegex.test(normalizedLeet)) {
      return true;
    }

    // Substring match on simplified string for longer profanities (>=4 chars) or clear evasions (e.g. f.u.c.k)
    if (word.length >= 4 && (simplified.includes(word) || normalizedLeet.includes(word))) {
      // Prevent false positives on harmless words like "class" or "pass" containing "ass"
      if (word === "ass" && !wordRegex.test(rawLower)) {
        continue;
      }
      return true;
    }
  }

  // 3. Check Arabic Profanity
  for (const word of ARABIC_PROFANITY) {
    const normWord = normalizeArabic(word).toLowerCase();

    // Exact word or boundary match
    if (
      normalizedAr === normWord ||
      simplified === normWord ||
      normalizedAr.includes(` ${normWord} `) ||
      normalizedAr.startsWith(`${normWord} `) ||
      normalizedAr.endsWith(` ${normWord}`)
    ) {
      return true;
    }

    // Substring match for Arabic profanities with length >= 3
    if (normWord.length >= 3 && (normalizedAr.includes(normWord) || simplified.includes(normWord))) {
      return true;
    }
  }

  return false;
};
